/**
 * The types defined here are used in tracking
 * Please be mindful if editing the value of the action type string
 * or adapting it's usage for some slightly different event
 * that this could affect how we analyse users.
 *
 * To ensure consistency, please contact whoever is managing
 * the analytics before making changes, so that updates
 * are transparent and we can adapt the respective systems.
 *
 * If a type is introduce purely for the sake of tracking,
 * it is ideal to prefix the type with "TRACKING/" to ensure
 * that it isn't removed in favour of redundancy.
 */

export enum TrackingType {
    init = "TRACKING/INIT",
    routeUpdated = "TRACKING/ROUTE_UPDATED",
    salesforce_request = "TRACKING/SALESFORCE/REQUEST",
    salesforce_newsletter = "TRACKING/SALESFORCE/NEWSLETTER",
    salesforce_contact = "TRACKING/SALESFORCE/CONTACT",
    billing_addMethod = "TRACKING/BILLING/ADD_FIRST_PAYMENT_METHOD",

    job_setCorrectness = "TRACKING/JOB/SET_CORRECTNESS",
    job_view = "TRACKING/JOB/VIEW"
}

export enum AuthUserType {
    startSignIn = "AUTH/USER/START_SIGN_IN",
    signedIn = "AUTH/USER/SIGNED_IN",
    startSignUp = "AUTH/USER/START_SIGN_UP",
    confirmSignup = "AUTH/USER/CONFIRM_SIGN_UP",
    signedUp = "AUTH/USER/SIGNED_UP",
    startSignOut = "AUTH/USER/START_SIGN_OUT",
    signedOut = "AUTH/USER/SIGNED_OUT",
    startResumeSession = "AUTH/USER/START_RESUME_SESSION",
    resumedSession = "AUTH/USER/RESUMED_SESSION",
    noSession = "AUTH/USER/NO_SESSION",
    verifyOTP = "AUTH/USER/VERIFY_OTP"
}

export enum MessagesType {
    set = "MESSAGES/SET"
}

export enum ClientType {
    inputRender_change = "CLIENT/INPUT_RENDER/CHANGE",
    inputRender_clear = "CLIENT/INPUT_RENDER/CLEAR",
    inputRender_interface_multipleChoice_set = "CLIENT/INPUT_RENDER/INTERFACE/MULTIPLE_CHOICE/SET",
    inputRender_dirtinessChanged = "CLIENT/INPUT_RENDER/DIRTINESS_CHANGED",
    inputRender_setVisualOnboarding = "CLIENT/INPUT_RENDER/SET_VISUAL_ONBOARDING"
}

export enum InstanceAPIType {
    setList = "API/APP_INSTANCE/SET_LIST",
    clearList = "API/APP_INSTANCE/CLEAR_LIST",
    setView = "API/APP_INSTANCE/SET_VIEW",
    addInstance = "API/APP_INSTANCE/ADD_INSTANCE",
    addError = "API/APP_INSTANCE/ADD_ERROR",
    baselineData_setList = "API/APP_INSTANCE/BASELINE_DATA/SET_LIST",
    baselineData_setTotalCount = "API/APP_INSTANCE/BASELINE_DATA/SET_TOTAL_COUNT",
    baselineData_setOne = "API/APP_INSTANCE/BASELINE_DATA/SET_ONE",
    baselineData_setError = "API/APP_INSTANCE/BASELINE_DATA/SET_ERROR",
    customizeMetrics_latestFetched = "API/APP_INSTANCE/CUSTOMIZE_METRICS/LATEST_METRICS_FETCHED",
    customizeMetrics_chooseMetricToOptimize = "API/APP_INSTANCE/CUSTOMIZE_METRICS/CHOOSE_METRIC_TO_OPTIMIZE",
    customizeMetrics_chooseQualityMetricToOptimize = "API/APP_INSTANCE/CUSTOMIZE_METRICS/CHOOSE_QUALITY_METRIC_TO_OPTIMIZE",
    setProcessingBatchList = "API/APP_INSTANCE/SET_PROCESSING_BATCH_LIST",
    startBatchSubmit = "API/APP_INSTANCE/START_BATCH_SUBMIT",
    progressBatchSubmit = "API/APP_INSTANCE/PROGRESS_BATCH_SUBMIT",
    setTagList = "API/APP_INSTANCE/SET_TAG_LIST",
    setPlansMetadata = "API/APP_INSTANCE/SET_PLANS_METADATA",
    setMeasurerConfiguration = "API/APP_INSTANCE/SET_MEASURER_CONFIGURATION",
    addJobWithFeedbackStats = "API/APP_INSTANCE/SET_JOB_WITH_FEEDBACK_STATS",
    resetUnreadMessagesCount = "APP_INSTANCE/RESET_UNREAD_MESSAGES_COUNT",
    setMetricsByAppID = "API/APP_INSTANCE/SET_METRICS",
    setAsyncOperations = "API/APP_INSTANCE/SET_ASYNC_OPERATIONS",
    setTotalJobCount = "API/APP_INSTANCE/SET_TOTAL_JOB_COUNT"
}

export enum APIAuthenticationType {
    setAPIKeys = "SET_API_KEYS",
    setUserInfo = "SET_USER_INFO",
    setCollaborationOwnerExists = "SET_COLLABORATION_OWNER_EXISTS",
    setOrgMemberOwnerExists = "SET_ORG_MEMBER_OWNER_EXISTS",
    setWorkerOwnerExists = "SET_WORKER_OWNER_EXISTS",
    setFreemiumPlanExpiryDays = "SET_FREEMIUM_PLAN_EXPIRY_DAYS"
}

export enum InterfaceType {
    appInstance_toggleInstructions = "INTERFACE/APP_INSTANCE/TOGGLE_INSTRUCTION",
    appInstance_addData = "INTERFACE/APP_INSTANCE/ADD_DATA",
    appInstance_clearData = "INTERFACE/APP_INSTANCE/CLEAR_DATA",
    appInstance_setColumnMap = "INTERFACE/APP_INSTANCE/SET_COLUMN_MAP",
    appInstance_clearColumnMap = "INTERFACE/APP_INSTANCE/CLEAR_COLUMN_MAP",
    appInstance_createExample = "INTERFACE/APP_INSTANCE/CREATE_EXAMPLE",
    appInstance_createBaseline_stage = "INTERFACE/APP_INSTANCE/CREATE_BASELINE/STAGE",
    main_toggleHeaderMenu = "INTERFACE/MAIN/TOGGLE_HEADER_MENU",
    client_formChange = "INTERFACE/CLIENT/FORM/CHANGE",
    client_formClear = "INTERFACE/CLIENT/FORM/CLEAR",
    client_formClearAll = "INTERFACE/CLIENT/FORM/CLEAR_ALL",
    uploader_enqueueUploads = "INTERFACE/CLIENT/UPLOADER/ENQUEUE_UPLOADS",
    uploader_finishUploads = "INTERFACE/CLIENT/UPLOADER/FINISH_UPLOAD",
    uploader_failUploads = "INTERFACE/CLIENT/UPLOADER/FAIL_UPLOAD",
    uploader_startUpload = "INTERFACE/CLIENT/UPLOADER/START_UPLOAD",
    uploader_removeQueueItem = "INTERFACE/CLIENT/UPLOADER/REMOVE_QUEUE_ITEM",
    uploader_progress = "INTERFACE/CLIENT/UPLOADER/PROGRESS"
}

export enum BatchEditType {
    enterBatchMode = "CLIENT/INTERFACE/BATCH_EDIT/ENTER",
    exitBatchMode = "CLIENT/INTERFACE/BATCH_EDIT/EXIT",
    addToSelection = "CLIENT/INTERFACE/BATCH_EDIT/ADD_SELECTION",
    removeFromSelection = "CLIENT/INTERFACE/BATCH_EDIT/REMOVE_SELECTION",
    clearSelection = "CLIENT/INTERFACE/BATCH_EDIT/CLEAR_SELECTION",
    toggleSelection = "CLIENT/INTERFACE/BATCH_EDIT/TOGGLE_SELECTION"
}

export enum JobAPIType {
    setList = "API/JOB/SET_LIST",
    setOne = "API/JOB/SET_ONE",
    setResponse = "API/JOB/SET_RESPONSE",
    setResponseError = "API/JOB/SET_RESPONSE_ERROR",
    download = "API/APP_INSTANCE/DOWNLOAD",
    clearDownload = "API/APP_INSTANCE/CLEAR_DOWNLOAD",
    downloadOperationId = "API/APP_INSTANCE/DOWNLOAD_OPERATION_ID",
    setDownloadOperation = "API/APP_INSTANCE/SET_DOWNLOAD_OPERATION",
    setJobIdToLabel = "API/JOB/SET_JOB_ID_TO_LABEL",
    setJobIdToReview = "API/JOB/SET_JOB_ID_TO_REVIEW",
    setJobCount = "API/JOB/SET_JOB_COUNT",
    invalidateJobCount = "API/JOB/INVALIDATE_JOB_COUNT",
    setJobConversation = "API/JOB/SET_JOB_CONVERSATION",
    addJobMessage = "API/JOB/ADD_JOB_MESSAGE",
    setFeedbackTags = "API/JOB/APP_FEEDBACK_TAGS",
    setMessageList = "API/JOB/SET_MESSAGE_LIST",
    setOneJobWithMessage = "API/JOB/SET_ONE_JOB_WITH_MESSAGE",
    setJobHistory = "API/JOB/SET_HISTORY",
    setJobPrelabels = "API/JOB/PRELABELS",
    setJobHistoryVersionResponse = "API/JOB/SET_HISTORY_VERSION_RESPONSE"
}

export enum TaskAPIType {
    setList = "API/TASK/SET_LIST",
    setDetails = "API/TASK/SET_DETAILS"
}

export enum MetricsAPIType {
    metricConstraintListFetchStateChanged = "API/METRICS/METRICS_CONSTRAINT_LIST",
    setMeasurerMetricList = "API/METRICS/MEASURER_METRICS_LIST_SET"
}

export enum EndpointsType {
    throttleFetch = "ENDPOINTS/THROTTLE_FETCH"
}

export enum ModalType {
    openModalView = "MODAL/OPEN_MODAL_VIEW",
    closeModalView = "MODAL/CLOSE_MODAL_VIEW"
}

export enum CollaboratorAPIType {
    setList = "API/COLLABORATOR/SET_LIST",
    metrics_setList = "API/COLLABORATOR/METRICS/SET_LIST"
}

export enum TemplateAPIType {
    setList = "API/APP_TEMPLATE/SET_LIST",
    addTemplates = "API/APP_TEMPLATE/ADD_TEMPLATES",
    receiveErrors = "API/APP_TEMPLATE/RECEIVE_ERRORS",
    setAvailability = "API/APP_TEMPLATE/SET_AVAILABILITY"
}

export enum BillingAPIType {
    invoices_setList = "API/BILLING/INVOICES/SET_LIST",
    paymentMethod_setList = "API/BILLING/PAYMENT_METHOD/SET_LIST"
}

export enum TagAPIType {
    setList = "API/TAG/SET_LIST"
}

export enum DataAPIType {
    signedUrlRequestStateChanged = "API/DATA/SIGNED_URL_REQUEST_STAGE_CHANGED",
    requestList = "API/DATA/REQUEST_LIST",
    receivedList = "API/DATA/RECEIVED_LIST"
}

export enum NetworkType {
    setDebounceState = "NETWORK/SET_DEBOUNCE_STATE",
    setPollState = "NETWORK/SET_POLL_STATE"
}

export enum RouteInfoType {
    showTimeoutExplanationModal = "ROUTE_INFO/SHOW_TIMEOUT_EXPLANATION_MODAL"
}

export enum DocsType {
    conceptsLoaded = "CLIENT/DOCS/DOC_LOADED",
    tocLoaded = "CLIENT/DOCS/TOC_LOADED"
}

export enum DataFileExplorerType {
    addData = "CLIENT/DATA_FILE_EXPLORER/ADD_DATA",
    clearData = "CLIENT/DATA_FILE_EXPLORER/CLEAR_DATA",
    setDataMapper = "CLIENT/DATA_FILE_EXPLORER/SET_DATA_MAPPER",
    clearDataMapper = "CLIENT/DATA_FILE_EXPLORER/CLEAR_DATA_MAPPER"
}

export enum OnboardingType {
    start = "ONBOARDING/START",
    clear = "ONBOARDING/CLEAR",
    setInstanceID = "ONBOARDING/SET_INSTANCE_ID",
    resetAttempt = "ONBOARDING/RESET_ATTEMPT",
    addAttempt = "ONBOARDING/ADD_ATTEMPT",
    setJobID = "ONBOARDING/SET_JOB_ID",
    toggleDemo = "ONBOARDING/TOGGLE_DEMO",
    setLabeler = "ONBOARDING/SET_LABELER",
    setSampleAppIdentifier = "ONBOARDING/SET_SAMPLE_APP_IDENTIFIER",
    setSampleAppData = "ONBOARDING/SET_SAMPLE_APP_DATA"
}

export enum VisualEditorType {
    detectAnnotations = "API/VISUAL_EDITOR/DETECT_ANNOTATIONS",
    receivedDetectedAnnotations = "API/VISUAL_EDITOR/RECEIVED_DETECTED_ANNOTATIONS",
    receivedDetectionErrors = "API/VISUAL_EDITOR/RECEIVED_DETECTION_ERRORS",
    getAppSchema = "API/VISUAL_EDITOR/GET_APP_SCHEMA",
    setAppSchema = "API/VISUAL_EDITOR/SET_APP_SCHEMA",
    setAppSchemaErrors = "API/VISUAL_EDITOR/SET_APP_SCHEMA_ERRORS"
}

export enum BrowserType {
    acceptCalibrationJob = "CLIENT/BROWSER/ACCEPT_CALIBRATION_JOB"
}

export enum ContentfulType {
    receiveDataProgramList = "EXTERNAL/CONTENTFUL/RECEIVE_DATA_PROGRAM_LIST",
    requestDataProgram = "EXTERNAL/CONTENTFUL/REQUEST_DATA_PROGRAM",
    receiveDataProgram = "EXTERNAL/CONTENTFUL/RECEIVE_DATA_PROGRAM",
    receiveDataProgramErrors = "EXTERNAL/CONTENTFUL/RECEIVE_DATA_PROGRAM_ERRORS",
    receiveTracking = "EXTERNAL/CONTENTFUL/RECEIVE_TRACKING",
    receiveTrackingErrors = "EXTERNAL/CONTENTFUL/RECEIVE_TRACKING_ERRORS"
}

export enum CollaboratorType {
    setProjectCollaborators = "API/COLLABORATOR/SET_PROJECT_COLLABORATORS",
    setCollaborator = "API/COLLABORATOR/SET_COLLABORATOR"
}

export enum InboxType {
    getInbox = "API/INBOX/GET_INBOX",
    createInbox = "API/INBOX/CREATE_INBOX",
    verifyInbox = "API/INBOX/VERIFY_INBOX",
    deleteInbox = "API/INBOX/DELETE_INBOX"
}

export enum BannerType {
    toggleInactiveWarning = "Banner/InactiveWarning"
}

export enum OrgMemberType {
    setMyOrgs = "API/ORG/SET_MY_ORGS",
    setOrgMembers = "API/ORG/SET_ORG_MEMBERS"
}

export enum WorkerType {
    setWorkerList = "API/WORKER/SET_WORKER_LIST",
    setAppWorkerList = "API/WORKER/SET_APP_WORKER_LIST",
    setAppWorker = "API/WORKER/SET_APP_WORKER"
}

export enum WorkerGroupType {
    setList = "API/WORKER_GROUP/SET_LIST",
    setAppWorkerGroupList = "API/WORKER_GROUP/SET_APP_WORKER_LIST"
}

export enum HeroType {
    set = "API/HERO/SET"
}

export enum BackofficeType {
    setCustomers = "API/BACKOFFICE/SET_CUSTOMERS",
    setCustomer = "API/BACKOFFICE/SET_CUSTOMER",
    setCustomerApps = "API/BACKOFFICE/SET_CUSTOMER_APPS"
}

export enum MarketplaceAppType {
    setApp = "API/MARKETPLACE/SET_APP",
    setTemplate = "API/MARKETPLACE/SET_TEMPLATE"
}

export enum SuperTasksAppType {
    setList = "API/SUPER_TASKS/SET_SUPER_TASKS_LIST",
    listRequest = "API/SUPER_TASKS/SUPER_TASKS_LIST_REQUEST",
    setListError = "API/SUPER_TASKS/SET_SUPER_TASKS_LIST_ERROR"
}

export enum StatisticsType {
    getStatisticsSnapshot = "API/STATISTICS/GET_SNAPSHOT"
}
